/**
 * Single source of truth for Scene Graph node discs (SceneGraphDiagram) so
 * other panels (e.g. Behavior Editor) can match appearance exactly.
 */

export const SCENE_GRAPH_NODE_RADIUS = {
  body: 22,
  joint: 11,
  mesh: 9,
  geom: 9,
  site: 9,
  /** Same layout/radius as `site`; separate graph type for MJCF `attachment_site` rows. */
  attachment_site: 9,
  inertial: 8,
  camera: 11,
  actuator: 11,
  /** Behavior editor open/close gripper nodes (sky cyan; not used by Scene Graph actuator satellites). */
  gripper: 11,
  tendon: 11,
  equality: 11,
} as const;

export type SceneGraphNodeType = keyof typeof SCENE_GRAPH_NODE_RADIUS;

export const SCENE_GRAPH_NODE_COLORS = {
  body: { base: "#374151", sel: "#3b82f6", stroke: "#6b7280", selStroke: "#60a5fa" },
  /** Hues match 3D joint center marker ({@link JOINT_COLORS.center} in viewer/joints/jointStyles.js). */
  joint: { base: "#0f766e", sel: "#0ddbb6", stroke: "#115e59", selStroke: "#5eead4" },
  mesh: { base: "#134e4a", sel: "#0d9488", stroke: "#0f766e", selStroke: "#2dd4bf" },
  geom: { base: "#450a0a", sel: "#dc2626", stroke: "#7f1d1d", selStroke: "#f87171" },
  /**
   * Darker magentas on the same hue as the default 3D selected site marker
   * (`#ff00ff`, `viewerStore` `siteVizSelectedColor`).
   */
  site: { base: "#6b126b", sel: "#981898", stroke: "#450a45", selStroke: "#b84eb8" },
  /** Slightly deeper magenta than `site` for attachment rows. */
  attachment_site: { base: "#5f145f", sel: "#8a188a", stroke: "#3d0a3d", selStroke: "#a848a8" },
  inertial: { base: "#1e3a5f", sel: "#2563eb", stroke: "#1e40af", selStroke: "#93c5fd" },
  camera: { base: "#052e16", sel: "#16a34a", stroke: "#166534", selStroke: "#4ade80" },
  /** Former scene-graph joint palette (warm orange). */
  actuator: { base: "#7c2d12", sel: "#d97706", stroke: "#92400e", selStroke: "#fbbf24" },
  gripper: { base: "#0c4a6e", sel: "#0ea5e9", stroke: "#075985", selStroke: "#7dd3fc" },
  tendon: { base: "#1f2937", sel: "#0891b2", stroke: "#374151", selStroke: "#67e8f9" },
  equality: { base: "#3b0764", sel: "#9333ea", stroke: "#6b21a8", selStroke: "#c084fc" },
} as const;

/** Three.js material color (`MeshBasicMaterial`, `LineBasicMaterial`) hex — matches scene graph camera disc `sel`. */
export const SAVED_CAMERA_MARKER_MESH_HEX = Number.parseInt(
  SCENE_GRAPH_NODE_COLORS.camera.sel.slice(1),
  16
);
/** Saved camera frustum wire color — matches scene graph camera `selStroke`. */
export const SAVED_CAMERA_MARKER_LINE_HEX = Number.parseInt(
  SCENE_GRAPH_NODE_COLORS.camera.selStroke.slice(1),
  16
);

const FALLBACK_FILL = { fill: "#374151", stroke: "#6b7280", strokeWidth: 1.5 };

/**
 * Mirrors {@code getNodeFill} in SceneGraphDiagram.jsx.
 */
export function sceneGraphNodeFillStyle(
  type: string,
  sel: boolean,
  batchSel: boolean
): { fill: string; stroke: string; strokeWidth: number } {
  const c = SCENE_GRAPH_NODE_COLORS[type as SceneGraphNodeType];
  if (!c) return FALLBACK_FILL;
  return {
    fill: sel ? c.sel : c.base,
    stroke: sel ? c.selStroke : batchSel ? "#fbbf24" : c.stroke,
    strokeWidth: type === "body" ? (sel || batchSel ? 3 : 2) : sel ? 2 : 1.5,
  };
}

export function sceneGraphIconSize(r: number): number {
  return r * 1.5;
}
